import React, {
  useEffect,
  useState,
} from "react";


/*
|--------------------------------------------------------------------------
| ALLOWED IMAGE TYPES
|--------------------------------------------------------------------------
*/

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];


const MAX_FILE_SIZE =
  5 * 1024 * 1024;


/*
|--------------------------------------------------------------------------
| OFFERING IMAGES
|--------------------------------------------------------------------------
*/

const OfferingImages = ({
  offeringId,
  images = [],
  loading = false,
  submitting = false,
  onCreate,
  onUpdate,
  onDelete,
  onRefresh,
}) => {

  /*
  |--------------------------------------------------------------------------
  | FORM
  |--------------------------------------------------------------------------
  */

  const [
    form,
    setForm
  ] = useState({
    file: null,
    altText: "",
    displayOrder: 0,
    isPrimary: false,
  });


  /*
  |--------------------------------------------------------------------------
  | PREVIEW
  |--------------------------------------------------------------------------
  */

  const [
    previewUrl,
    setPreviewUrl
  ] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | FORM VISIBILITY
  |--------------------------------------------------------------------------
  */

  const [
    showForm,
    setShowForm
  ] = useState(false);


  /*
  |--------------------------------------------------------------------------
  | ERROR
  |--------------------------------------------------------------------------
  */

  const [
    errorMessage,
    setErrorMessage
  ] = useState("");


  /*
  |--------------------------------------------------------------------------
  | RESET FORM
  |--------------------------------------------------------------------------
  */

  const resetForm = () => {

    setForm({
      file: null,
      altText: "",
      displayOrder: 0,
      isPrimary: false,
    });


    setPreviewUrl(null);


    setErrorMessage("");

  };


  /*
  |--------------------------------------------------------------------------
  | CLEAN PREVIEW URL
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    return () => {

      if (previewUrl) {

        URL.revokeObjectURL(
          previewUrl
        );

      }

    };

  }, [
    previewUrl
  ]);


  /*
  |--------------------------------------------------------------------------
  | CHANGE
  |--------------------------------------------------------------------------
  */

  const handleChange = (
    event
  ) => {

    const {
      name,
      value,
      type,
      checked,
      files,
    } = event.target;


    /*
    |--------------------------------------------------------------------------
    | FILE
    |--------------------------------------------------------------------------
    */

    if (type === "file") {

      const file =
        files?.[0] || null;


      setErrorMessage("");


      /*
      |--------------------------------------------------------------------------
      | FILE TYPE
      |--------------------------------------------------------------------------
      */

      if (
        file &&
        !ALLOWED_IMAGE_TYPES.includes(
          file.type
        )
      ) {

        setForm(
          previous => ({
            ...previous,
            file: null,
          })
        );


        if (previewUrl) {

          URL.revokeObjectURL(
            previewUrl
          );

        }


        setPreviewUrl(null);


        setErrorMessage(
          "Invalid image type. Please select a JPG, JPEG, PNG or WebP image."
        );


        event.target.value = "";


        return;

      }


      /*
      |--------------------------------------------------------------------------
      | FILE SIZE
      |--------------------------------------------------------------------------
      */

      if (
        file &&
        file.size > MAX_FILE_SIZE
      ) {

        setForm(
          previous => ({
            ...previous,
            file: null,
          })
        );


        if (previewUrl) {

          URL.revokeObjectURL(
            previewUrl
          );

        }


        setPreviewUrl(null);


        setErrorMessage(
          "Image size cannot exceed 5 MB."
        );


        event.target.value = "";


        return;

      }


      setForm(
        previous => ({
          ...previous,
          file,
        })
      );


      if (previewUrl) {

        URL.revokeObjectURL(
          previewUrl
        );

      }


      if (file) {

        setPreviewUrl(
          URL.createObjectURL(
            file
          )
        );

      } else {

        setPreviewUrl(null);

      }


      return;

    }


    /*
    |--------------------------------------------------------------------------
    | OTHER FIELDS
    |--------------------------------------------------------------------------
    */

    setForm(
      previous => ({
        ...previous,

        [name]:
          type === "checkbox"
            ? checked
            : value,
      })
    );

  };


  /*
  |--------------------------------------------------------------------------
  | SUBMIT
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async (
    event
  ) => {

    event.preventDefault();


    setErrorMessage("");


    /*
    |--------------------------------------------------------------------------
    | OFFERING ID
    |--------------------------------------------------------------------------
    */

    if (!offeringId) {

      setErrorMessage(
        "Unable to upload image because the offering ID is missing."
      );


      return;

    }


    /*
    |--------------------------------------------------------------------------
    | FILE
    |--------------------------------------------------------------------------
    */

    if (!form.file) {

      setErrorMessage(
        "Please select an image before uploading."
      );


      return;

    }


    /*
    |--------------------------------------------------------------------------
    | FILE TYPE
    |--------------------------------------------------------------------------
    */

    if (
      !ALLOWED_IMAGE_TYPES.includes(
        form.file.type
      )
    ) {

      setErrorMessage(
        "Invalid image type. Please select a JPG, JPEG, PNG or WebP image."
      );


      return;

    }


    /*
    |--------------------------------------------------------------------------
    | FILE SIZE
    |--------------------------------------------------------------------------
    */

    if (
      form.file.size > MAX_FILE_SIZE
    ) {

      setErrorMessage(
        "Image size cannot exceed 5 MB."
      );


      return;

    }


    /*
    |--------------------------------------------------------------------------
    | FORM DATA
    |--------------------------------------------------------------------------
    */

    const formData =
      new FormData();


    /*
    |--------------------------------------------------------------------------
    | IMAGE
    |--------------------------------------------------------------------------
    |
    | Must match:
    |
    | upload.single("image")
    |
    */

    formData.append(
      "image",
      form.file
    );


    /*
    |--------------------------------------------------------------------------
    | ALT TEXT
    |--------------------------------------------------------------------------
    */

    if (
      form.altText.trim()
    ) {

      formData.append(
        "altText",
        form.altText.trim()
      );

    }


    /*
    |--------------------------------------------------------------------------
    | DISPLAY ORDER
    |--------------------------------------------------------------------------
    */

    formData.append(
      "displayOrder",
      String(
        Number(form.displayOrder) || 0
      )
    );


    /*
    |--------------------------------------------------------------------------
    | PRIMARY
    |--------------------------------------------------------------------------
    */

    formData.append(
      "isPrimary",
      String(
        form.isPrimary
      )
    );


    try {

      await onCreate?.(
        formData
      );


      resetForm();


      setShowForm(
        false
      );


      if (
        typeof onRefresh ===
        "function"
      ) {

        await onRefresh();

      }

    } catch (error) {

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;


      setErrorMessage(
        backendMessage ||
        "Failed to upload image. Please try again."
      );

    }

  };


  /*
  |--------------------------------------------------------------------------
  | DELETE
  |--------------------------------------------------------------------------
  */

  const handleDelete = async (
    image
  ) => {

    if (!image?.id) {

      return;

    }


    const confirmed =
      window.confirm(
        "Are you sure you want to delete this image?"
      );


    if (!confirmed) {

      return;

    }


    setErrorMessage("");


    try {

      await onDelete?.(
        image
      );


      if (
        typeof onRefresh ===
        "function"
      ) {

        await onRefresh();

      }

    } catch (error) {

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;


      setErrorMessage(
        backendMessage ||
        "Failed to delete offering image."
      );

    }

  };


  /*
  |--------------------------------------------------------------------------
  | MAKE PRIMARY
  |--------------------------------------------------------------------------
  */

  const handleMakePrimary = async (
    image
  ) => {

    if (!image?.id) {

      return;

    }


    setErrorMessage("");


    try {

      await onUpdate?.(
        image.id,
        {
          isPrimary: true,
        }
      );


      if (
        typeof onRefresh ===
        "function"
      ) {

        await onRefresh();

      }

    } catch (error) {

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;


      setErrorMessage(
        backendMessage ||
        "Failed to update offering image."
      );

    }

  };


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {

    return (

      <section className="offering-images">

        <h3>
          Images
        </h3>

        <p>
          Loading images...
        </p>

      </section>

    );

  }


  /*
  |--------------------------------------------------------------------------
  | PAGE
  |--------------------------------------------------------------------------
  */

  return (

    <section className="offering-images">


      {/* HEADER */}

      <div className="section-header">

        <div>

          <h3>
            Images
          </h3>

          <span>
            {images.length} image
            {images.length !== 1
              ? "s"
              : ""}
          </span>

        </div>


        <button
          type="button"
          onClick={() => {

            setErrorMessage("");


            setShowForm(
              previous =>
                !previous
            );

          }}
        >

          {showForm
            ? "Cancel"
            : "Add Image"}

        </button>

      </div>


      {/* ERROR */}

      {errorMessage && (

        <div
          className="error-message"
          role="alert"
        >

          {errorMessage}

        </div>

      )}


      {/* ADD IMAGE FORM */}

      {showForm && (

        <form
          className="image-form"
          onSubmit={
            handleSubmit
          }
        >


          {/* FILE */}

          <div className="form-group">

            <label>
              Image *
            </label>

            <input
              type="file"
              name="image"
              accept="image/jpeg,image/png,image/webp"
              onChange={
                handleChange
              }
              required
            />

            <small>
              JPG, JPEG, PNG or WebP. Maximum 5 MB.
            </small>

          </div>


          {/* PREVIEW */}

          {previewUrl && (

            <div
              className="image-upload-preview"
            >

              <img
                src={previewUrl}
                alt="Selected preview"
              />

            </div>

          )}


          {/* ALT TEXT */}

          <div className="form-group">

            <label>
              Alt Text
            </label>

            <input
              type="text"
              name="altText"
              value={
                form.altText
              }
              onChange={
                handleChange
              }
              placeholder="Describe the image"
              maxLength={255}
            />

          </div>


          {/* DISPLAY ORDER */}

          <div className="form-group">

            <label>
              Display Order
            </label>

            <input
              type="number"
              min="0"
              step="1"
              name="displayOrder"
              value={
                form.displayOrder
              }
              onChange={
                handleChange
              }
            />

          </div>


          {/* PRIMARY */}

          <label
            className="checkbox-group"
          >

            <input
              type="checkbox"
              name="isPrimary"
              checked={
                form.isPrimary
              }
              onChange={
                handleChange
              }
            />

            <span>
              Set as primary image
            </span>

          </label>


          {/* SUBMIT */}

          <button
            type="submit"
            disabled={
              submitting ||
              !form.file
            }
          >

            {submitting
              ? "Uploading..."
              : "Upload Image"}

          </button>

        </form>

      )}


      {/* EMPTY */}

      {!images.length ? (

        <div className="empty-state">

          No images added to this offering.

        </div>

      ) : (

        <div className="offering-images-grid">

          {images.map(
            image => {

              const publicUrl =
                image.public_url ||
                image.publicUrl;


              const altText =
                image.alt_text ||
                image.altText;


              const originalFilename =
                image.original_filename ||
                image.originalFilename;


              const isPrimary =
                image.is_primary ||
                image.isPrimary;


              return (

                <div
                  className="offering-image-card"
                  key={image.id}
                >


                  {/* IMAGE */}

                  <div
                    className="image-preview"
                  >

                    {publicUrl ? (

                      <img
                        src={publicUrl}
                        alt={
                          altText ||
                          originalFilename ||
                          "Offering image"
                        }
                      />

                    ) : (

                      <div
                        className="image-placeholder"
                      >

                        No Preview

                      </div>

                    )}


                    {/* PRIMARY */}

                    {isPrimary && (

                      <span
                        className="primary-badge"
                      >

                        Primary

                      </span>

                    )}

                  </div>


                  {/* CONTENT */}

                  <div
                    className="image-card-content"
                  >

                    <strong>

                      {
                        originalFilename ||
                        "Offering image"
                      }

                    </strong>


                    {altText && (

                      <p>
                        {altText}
                      </p>

                    )}


                    {/* ACTIONS */}

                    <div
                      className="image-card-actions"
                    >

                      {!isPrimary && (

                        <button
                          type="button"
                          onClick={() =>
                            handleMakePrimary(
                              image
                            )
                          }
                          disabled={
                            submitting
                          }
                        >

                          Make Primary

                        </button>

                      )}


                      <button
                        type="button"
                        className="danger"
                        onClick={() =>
                          handleDelete(
                            image
                          )
                        }
                        disabled={
                          submitting
                        }
                      >

                        Delete

                      </button>

                    </div>

                  </div>

                </div>

              );

            }
          )}

        </div>

      )}

    </section>

  );

};


export default OfferingImages;